const hiddenClass = 'selling_plan_theme_integration--hidden';

class SellingPlansWidget {
  constructor(sellingPlansWidgetContainer) {
    this.enablePerformanceObserver();
    this.sellingPlansWidgetContainer = sellingPlansWidgetContainer;
    // this.appendSellingPlanInputs();
    this.updateSellingPlanInputsValues();
    this.listenToVariantChange();
    this.listenToSellingPlanFormRadioButtonChange();
    this.updateSelectedOptions();
    if (this.selectedPurchaseOption?.dataset?.variantId && this.selectedPurchaseOption?.dataset?.sellingPlanId) {
      this.updateSelectedVisual(this.selectedPurchaseOption.dataset.variantId, this.selectedPurchaseOption.dataset.sellingPlanId);
      this.updatePrice(this.selectedPurchaseOption.dataset.variantId, this.selectedPurchaseOption.dataset.sellingPlanId);
    }
  }

  get sectionId() {
    return this.sellingPlansWidgetContainer.getAttribute('data-section-id');
  }

  get shopifySection() {
    return document.querySelector(`#shopify-section-${this.sectionId}`);
  }

  /*
    We are careful to target the correct form, as there are instances when we encounter an installment form that we specifically aim to avoid interacting with.
  */
  get variantIdInput() {
    return (
      this.addToCartForms[1]?.querySelector(`input[name="id"]`) ||
      this.addToCartForms[1]?.querySelector(`select[name="id"]`) ||
      this.addToCartForms[0].querySelector(`input[name="id"]`) ||
      this.addToCartForms[0].querySelector(`select[name="id"]`)
    );
  }

  get priceElement() {
    return this.shopifySection.querySelector('.price');
  }

  get comparedAtPrice() {
    return this.shopifySection.querySelector('.price__sale');
  }

  get visibleSellingPlanForm() {
    return this.shopifySection.querySelector(
      `section[data-variant-id^="${this.variantIdInput?.value}"]`,
    );
  }

  get isVariantAvailable() {
    return this.selectedPurchaseOption.getAttributeNames().includes('disabled');
  }

  get sellingPlanInput() {
    return this.shopifySection.querySelector('.selected-selling-plan-id');
  }

  get addToCartForms() {
    return this.shopifySection.querySelectorAll('[action*="/cart/add"]');
  }

  /*
    To enable the addition of a selling plan to a cart, it's necessary to include an input with the name "selling_plan", which will carry the selling ID as its value. When a buyer clicks on 'add to cart', the appropriate selling plan ID is added to their cart.
  */
  appendSellingPlanInputs() {
    this.addToCartForms.forEach((addToCartForm) => {
      addToCartForm.appendChild(this.sellingPlanInput.cloneNode());
    });
  }

  showSellingPlanForm(sellingPlanFormForSelectedVariant) {
    sellingPlanFormForSelectedVariant?.classList?.remove(hiddenClass);
  }

  hideSellingPlanForms(sellingPlanFormsForUnselectedVariants) {
    sellingPlanFormsForUnselectedVariants.forEach((element) => {
      element.classList.add(hiddenClass);
    });
  }

  /*
    Each product variant comes with a selling plan selection box that the buyer can interact with.
    When a buyer chooses a different variant, we ensure that only the relevant selling plan selection box is displayed.
    This guarantees that only the selling plan associated with the selected variant is shown.
  */
  handleSellingPlanFormVisibility() {
    const sellingPlanFormForSelectedVariant = this.shopifySection.querySelector(
      `section[data-variant-id="${this.variantIdInput.value}"]`,
    );
    const sellingPlanFormsForUnselectedVariants =
      this.shopifySection.querySelectorAll(
        `.selling_plan_theme_integration:not([data-variant-id="${this.variantIdInput.value}"])`,
      );
    this.showSellingPlanForm(sellingPlanFormForSelectedVariant);
    this.hideSellingPlanForms(sellingPlanFormsForUnselectedVariants);
  }

  handleVariantChange() {
    this.handleSellingPlanFormVisibility();
    this.updateSellingPlanInputsValues();
    this.listenToSellingPlanFormRadioButtonChange();
  }

  /*
    The functions listenToVariantChange() and listenToAddToCartForms() are implemented to track when a product variant is altered or when the product form is updated.
    The identification of the variant is crucial as it dictates which selling plan box should be displayed.
  */
  listenToVariantChange() {
    this.listenToAddToCartForms();
    const variantIdInputInterval = setInterval(() => {
      if (this.variantIdInput && this.variantIdInput.tagName === 'INPUT') {
        const variantIdObserver = new MutationObserver((mutationList) => {
          mutationList.forEach((mutationRecord) => {
            this.handleVariantChange(mutationRecord.target.value);
          });
        });
    
        variantIdObserver.observe(this.variantIdInput, {
          attributes: true,
        });

        clearInterval(variantIdInputInterval);
      }
    }, 200);
  }

  listenToAddToCartForms() {
    this.addToCartForms.forEach((addToCartForm) => {
      addToCartForm.addEventListener('change', () => {
        this.handleVariantChange();
      });
    });
  }

  get regularPriceElement() {
    return this.shopifySection.querySelector('.price__regular');
  }

  get salePriceElement() {
    return this.shopifySection.querySelector('.price__sale');
  }

  get salePriceValue() {
    return this.salePriceElement.querySelector('.price-item--sale');
  }

  get regularPriceValue() {
    return this.salePriceElement.querySelector('.price-item--regular');
  }

  get sellingPlanAllocationPrice() {
    return document.getElementById(
      `${this.selectedPurchaseOption.dataset.sellingPlanGroupId}_allocation_price`,
    );
  }

  get selectedPurchaseOptionPrice() {
    return this.selectedPurchaseOption.dataset.variantPrice;
  }

  get selectedPurchaseOptionComparedAtPrice() {
    return this.selectedPurchaseOption.dataset.variantCompareAtPrice;
  }

  get price() {
    return this.sellingPlanAllocationPrices.price ?? null;
  }

  getSelectedPurchasePlan(variantId, sellingPlanId) {
    let selectedPurchaseOption = null;
    if (variantId) {
      if (sellingPlanId) {
        selectedPurchaseOption = document.querySelector(`[data-variant-id="${variantId}"][data-selling-plan-id="${sellingPlanId}"][data-radio-type="selling-plan"]`);
      } else {
        selectedPurchaseOption = document.querySelector(`[data-variant-id="${variantId}"][data-radio-type="one-time-purchase"`);
      }
    } else {
      return;
    }
    return selectedPurchaseOption;
  }

  updatePrice(variantId, sellingPlanId=null) {
    const selectedPurchaseOption = this.getSelectedPurchasePlan(variantId, sellingPlanId);
    const selectedPurchaseOptionPrice = selectedPurchaseOption.dataset.variantPrice;
    const selectedPurchaseOptionPriceRaw = selectedPurchaseOption.dataset.variantPriceRaw;

    const priceContainer = document.querySelector('.js-price-container')
    if (selectedPurchaseOptionPrice && priceContainer) {
      priceContainer.innerHTML = selectedPurchaseOptionPrice;
    }

    const rewardPointsContainer = document.querySelector('.js-reward-points-container')
    const rewardPointsElement = document.querySelector('.js-reward-points')
    if (selectedPurchaseOptionPriceRaw && rewardPointsContainer && rewardPointsElement) {
      const perRewardPoints = Number(rewardPointsContainer.dataset.currencyPerRewardPoint ?? 0)
      const rewardPoints = Math.floor((Number(selectedPurchaseOptionPriceRaw) / 100)) * perRewardPoints;
      rewardPointsElement.innerHTML = rewardPoints;
    }

    const subscribeAndSavePriceContainer = document.querySelector('[data-subscribe-and-save-price]');
    if (selectedPurchaseOptionPrice && subscribeAndSavePriceContainer && sellingPlanId) {
      subscribeAndSavePriceContainer.innerHTML = selectedPurchaseOptionPrice;
    }
  }

  updateSelectedVisual(variantId, sellingPlanId) {
    const selectedPurchaseOption = this.getSelectedPurchasePlan(variantId, sellingPlanId);
    const targetSellingPlanInputs = document.querySelectorAll('.product-selling-plan__option');
    if (targetSellingPlanInputs.length > 0) {
      targetSellingPlanInputs.forEach((targetSellingPlanInput) => {
        targetSellingPlanInput.classList.remove('product-selling-plan__option--selected');
        if (targetSellingPlanInput.classList.contains(`product-selling-plan__option--${selectedPurchaseOption.dataset.radioType}`)) {
          targetSellingPlanInput.classList.add('product-selling-plan__option--selected');
        }
      })
    }

    const targetSellingPlanDropdowns = document.querySelectorAll('.product-selling-plan__dropdown-option');
    const targetSellingPlanSelector = document.querySelector('.product-selling-plan__dropdown-selector');
    if (targetSellingPlanDropdowns.length > 0) {
      targetSellingPlanDropdowns.forEach((targetSellingPlanDropdown) => {
        targetSellingPlanDropdown.classList.remove('product-selling-plan__dropdown-option--selected');
        if (targetSellingPlanDropdown.dataset.sellingPlanId === sellingPlanId) {
          targetSellingPlanDropdown.classList.add('product-selling-plan__dropdown-option--selected');
          targetSellingPlanSelector.innerHTML = targetSellingPlanDropdown.dataset.sellingPlanName;
        }
      })
    }

    const sellingPlanInputs = document.querySelectorAll('.selected-selling-plan-id');
    if(sellingPlanInputs.length > 0) {
      sellingPlanInputs.forEach((sellingPlanInput) => {
        sellingPlanInput.value = sellingPlanId;
      });
    }
    
    const targetSelectedHTML = document.querySelectorAll('.product-selling-plan__option--selected');
    if (!targetSelectedHTML) this.updateSelectedVisual(variantId, sellingPlanId);

    const errorMessage = document.querySelector('.product-selling-plan__dropdown-error')
    if (errorMessage) {
      errorMessage.classList.remove('product-selling-plan__dropdown-error--active')
    }

    return;
  }

  updateUrl(variantId, sellingPlanId) {
    const url = new URL(window.location.href);

    if (variantId && sellingPlanId) {
      url.searchParams.set('variant', variantId);
    } else {
      url.searchParams.delete('variant');
    }

    if (sellingPlanId) {
      url.searchParams.set('selling_plan', sellingPlanId);
    } else {
      url.searchParams.delete('selling_plan');
    }

    window.history.replaceState({}, '', url);
  }

  updateSelectedOptions () {
    const sellingPlanInterval = setInterval(() => {
      const loadingElements = document.querySelectorAll('.product-selling-plan__option--loading');
      if (loadingElements.length > 0) {
        loadingElements.forEach((productSellingPlan) => {
          productSellingPlan.classList.remove('product-selling-plan__option--loading');
        });
      }

      if (this.sellingPlanOptions.length > 0) {
        const params = new URLSearchParams(window.location.search);
        let variantId = params.get('variant');
        const sellingPlanId = params.get('selling_plan');

        if (!variantId) {
          const sellingPlanContainer = document.querySelector(".product-selling-plan__theme-integration")
          if (sellingPlanContainer) {
            variantId = sellingPlanContainer.dataset.variantId
          }
        }
        this.updateSelectedVisual(variantId, sellingPlanId);
        this.updatePrice(variantId, sellingPlanId);

        const selectedElements = document.querySelectorAll('.product-selling-plan__option--selected');
        if (selectedElements.length > 0) {
          clearInterval(sellingPlanInterval);

          const paymentButtons = document.querySelectorAll('.selling-plan__option--loading');
          if (paymentButtons.length > 0) {
            paymentButtons.forEach((paymentButton) => {
              paymentButton.classList.remove('selling-plan__option--loading');
            });
          }
        }
      }
    }, 200);
  }

  hideSalePrice() {
    this.salePriceElement.style.display = 'none';
  }

  hideRegularPrice() {
    this.regularPriceElement.style.display = 'none';
  }

  showRegularPrice() {
    this.regularPriceElement.style.display = 'block';
    this.shopifySection.querySelector('.price__sale').style.display = 'none';
  }

  showSalePrice() {
    this.salePriceElement.style.display = 'block';
    this.regularPriceValue.innerHTML =
      this.selectedPurchaseOptionComparedAtPrice;
    this.salePriceValue.innerHTML = this.selectedPurchaseOptionPrice;
  }

  get sellingPlanInputs() {
    return this.shopifySection.querySelectorAll('.selected-selling-plan-id');
  }

  get sellingPlanOptions() {
    return this.shopifySection.querySelectorAll('.selling-plan-option');
  }

  get oneTimePlanOption() {
    return this.shopifySection.querySelector('.selling-plan-option.selling-plan-option--one-time');
  }

  updateSellingPlanInputsValues() {
    this.sellingPlanInputs.forEach((sellingPlanInput) => {
      sellingPlanInput.value = this.sellingPlanInputValue;
    });
  }

  get sellingPlanInputValue() {
    return this.selectedPurchaseOption?.dataset.sellingPlanId ?? null;
  }

  get selectedPurchaseOption() {
    return this.visibleSellingPlanForm?.querySelector(
      'input[type="radio"]:checked',
    );
  }

  set selectedPurchaseOption(selectedPurchaseOption) {
    this._selectedPurchaseOption = selectedPurchaseOption;
  }

  handleRadioButtonChange(selectedPurchaseOption) {
    this.selectedPurchaseOption = selectedPurchaseOption;
    this.updateSellingPlanInputsValues();

    const { variantId, sellingPlanId } = selectedPurchaseOption.dataset
    this.updateUrl(variantId, sellingPlanId);
    this.updateSelectedVisual(variantId, sellingPlanId);
    this.updatePrice(variantId, sellingPlanId);

    document.querySelector('.product-selling-plan__dropdown-options').classList.remove('product-selling-plan__dropdown-options--active');
    if (typeof(sellingPlanId) != "undefined") {
      document.querySelector('.product-selling-plan__option--selling-plan .product-selling-plan__option-label').setAttribute("for", `selling-plan-option-${sellingPlanId}`);
    }
  }

  listenToSellingPlanFormRadioButtonChange() {
    this.visibleSellingPlanForm
      ?.querySelectorAll('input[type="radio"]')
      .forEach((radio) => {
        radio.addEventListener('change', (event) => {
          this.handleRadioButtonChange(event.target);
        });
      });
  }

  enablePerformanceObserver() {
    const performanceObserver = new PerformanceObserver((list) => {
      list.getEntries().forEach((entry) => {
        if (entry.initiatorType !== 'fetch') return;

        const url = new URL(entry.name);
        /*
          When a buyer selects a product variant, a fetch request is initiated.
          Upon completion of this fetch request, we update the price to reflect the correct value.
        */
        if (url.search.includes('variant') || url.search.includes('variants')) {
          // TODO
          // this.updateSelectedVisual();
          // this.updatePrice();
        }
      });
    });

    performanceObserver.observe({entryTypes: ['resource']});
  }
}

document
  .querySelectorAll('.selling_plan_app_container')
  .forEach((sellingPlansWidgetContainer) => {
    new SellingPlansWidget(sellingPlansWidgetContainer);
  });

function initSellingPlanDropdown() {
  const selector = document.querySelector('.product-selling-plan__dropdown-selector');
  const dropdownOptions = document.querySelector('.product-selling-plan__dropdown-options');

  selector.addEventListener('click', function(event) {
    event.stopPropagation();
    dropdownOptions.classList.toggle('product-selling-plan__dropdown-options--active');
  });

  document.addEventListener('click', function(event) {
    if (!dropdownOptions.contains(event.target) && !selector.contains(event.target)) {
        dropdownOptions.classList.remove('product-selling-plan__dropdown-options--active');
    }
  });
}

if (document.readyState !== "loading") {
  initSellingPlanDropdown();
} else {
  document.addEventListener("DOMContentLoaded", function() {
    initSellingPlanDropdown();
  });
}
